import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, Search, Share2, Bell, Images, Check, ArrowRight } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1777652918682-dec5c79d1301?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNhcmQlMjBleGVjdXRpdmUlMjBuZXR3b3JraW5nfGVufDB8fHx8MTc5MDA5OTE5Mnww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: ScanLine, title: "AI scan, front & back", text: "Snap a card and Dextyou auto-fills name, title, company, phone, email, website and socials." },
  { icon: Search, title: "Find anyone fast", text: "Search by name, company or industry. Star favorites and filter your whole network instantly." },
  { icon: Share2, title: "Share in a tap", text: "Send a contact by QR, vCard, SMS or email so the right person gets the details instantly." },
  { icon: Bell, title: "Never drop a lead", text: "Set follow-up reminders and get an email nudge the moment they're due — even with the app closed." },
];

const PLANS = [
  { name: "Monthly", price: "$0.99", per: "/month", note: "Billed monthly. Cancel anytime.", highlight: false },
  { name: "Yearly", price: "$9.99", per: "/year", note: "2 months free vs monthly.", highlight: true },
];

function Nav() {
  return (
    <header className="glass sticky top-0 z-40 px-4 sm:px-6 py-3">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0A2540] to-[#2EA6FF] flex items-center justify-center shadow-lg shadow-blue-900/30">
            <span className="font-heading text-white font-extrabold text-lg leading-none">D</span>
          </div>
          <div>
            <span className="font-heading text-lg font-extrabold tracking-tight leading-none block">Dextyou</span>
            <span className="text-[10px] text-[#2EA6FF] font-mono tracking-wider">LET ME DEXTYOU</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link data-testid="landing-signin" to="/login" className="text-sm text-slate-300 hover:text-white transition-colors">
            Sign in
          </Link>
          <Link
            data-testid="landing-get-started"
            to="/register"
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-4 py-2 text-sm transition-colors"
          >
            Get started <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A1524] text-white">
      <Nav />

      {/* Hero */}
      <section className="dotted-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="overline text-[#2EA6FF] mb-3">Let Me Dextyou</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Snap a card.<br />
              <span className="text-[#2EA6FF]">Skip the typing.</span>
            </h1>
            <p className="text-slate-400 mt-5 text-base sm:text-lg leading-relaxed max-w-xl">
              Dextyou turns a photo of any business card into an organized, searchable contact — then lets you
              share it, remind yourself to follow up, and never lose a connection again.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link
                data-testid="landing-hero-cta"
                to="/register"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-6 py-3.5 transition-colors shadow-lg shadow-blue-900/30"
              >
                <ScanLine className="w-5 h-5" /> Start scanning
              </Link>
              <Link
                to="/login"
                className="flex items-center gap-2 border border-slate-700 hover:border-[#2EA6FF] text-white font-semibold rounded-xl px-6 py-3.5 transition-colors"
              >
                I have an account
              </Link>
            </div>
            <p className="font-mono text-xs text-slate-500 tracking-wider mt-6">
              CAPTURE &nbsp;·&nbsp; ORGANIZE &nbsp;·&nbsp; SHARE &nbsp;·&nbsp; CONNECT
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-[#2EA6FF]/10 blur-3xl rounded-full" />
            <img src="/dextyou-logo.png" alt="Dextyou" className="relative w-full max-w-md mx-auto rounded-3xl bg-white p-6 shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-center mb-3">
          Your whole network, in your pocket
        </h2>
        <p className="text-slate-400 text-center max-w-2xl mx-auto mb-12">
          Everything you need to capture, organize and act on the people you meet.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-slate-800 bg-[#0F2138] p-6 hover:border-[#2EA6FF]/50 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[#2EA6FF]" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Bulk highlight */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-[#0F2138] to-[#0A1524] p-8 sm:p-12 flex flex-col sm:flex-row items-center gap-8">
          <div className="w-16 h-16 shrink-0 rounded-2xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center">
            <Images className="w-8 h-8 text-[#2EA6FF]" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-heading text-2xl font-extrabold tracking-tight mb-1.5">Back from an event with a stack of cards?</h3>
            <p className="text-slate-400">Bulk-scan up to 20 at once — Dextyou reads them all, you review, and add them in one go.</p>
          </div>
          <Link to="/register" className="shrink-0 flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-6 py-3 transition-colors">
            Try it free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-center mb-3">Simple pricing</h2>
        <p className="text-slate-400 text-center mb-12">Full access. No limits. Cancel anytime.</p>
        <div className="grid sm:grid-cols-2 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`rounded-2xl p-7 border ${
                p.highlight ? "border-[#2EA6FF] bg-gradient-to-b from-blue-500/10 to-[#0F2138]" : "border-slate-800 bg-[#0F2138]"
              }`}
            >
              {p.highlight && (
                <span className="inline-block text-[11px] font-semibold bg-blue-600 text-white px-3 py-1 rounded-full mb-3">Best value</span>
              )}
              <h3 className="font-heading text-lg font-semibold text-slate-300">{p.name}</h3>
              <div className="flex items-end gap-1 mt-2 mb-1">
                <span className="font-heading text-4xl font-extrabold tracking-tight">{p.price}</span>
                <span className="text-slate-400 mb-1 text-sm">{p.per}</span>
              </div>
              <p className="text-xs text-slate-500 mb-6">{p.note}</p>
              <ul className="space-y-2.5 mb-7">
                {["AI scan — front & back", "Unlimited contacts", "Search & share", "Reminders + email nudges"].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-[#2EA6FF] shrink-0 mt-0.5" /> {x}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className={`block text-center w-full py-3 rounded-xl font-semibold transition-colors ${
                  p.highlight ? "bg-blue-600 hover:bg-blue-500 text-white" : "border border-slate-700 text-white hover:border-[#2EA6FF]"
                }`}
              >
                Get {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0A2540] to-[#2EA6FF] flex items-center justify-center">
              <span className="font-heading text-white font-extrabold leading-none">D</span>
            </div>
            <span className="font-heading font-extrabold tracking-tight">Dextyou</span>
            <span className="text-xs text-slate-500 ml-1">· Let Me Dextyou</span>
          </div>
          <p className="text-xs text-slate-600">© {new Date().getFullYear()} Dextyou. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
